import { usEvidenceNow } from './timeEvidence';
import { usGameSportForKey } from './sports';
/**
 * #1016 — which of a trader's saved Polymarket US orders belong to one board
 * game, for Game Detail's open orders and "Cancel this game".
 *
 * A saved US order carries only its market slug and outcome, never a game key.
 * The game comes from the relay's US catalogue: the order's exact slug must be
 * listed there with the exact five-part game key of this board game, under a
 * classification that has not expired. Nothing is inferred from a label, a
 * team pair, a price or a slug substring (search is a substring match, so only
 * exact slug equality counts). Book state is not identity: a market without a
 * current book still names its game.
 *
 * Same one-directional rule as the K/P selector (lib/trading/gameOrderSelection):
 * an order the catalogue ties to this matchup but not to exactly this game
 * BLOCKS the game action, because sweeping the easy subset leaves exposure the
 * trader believes is gone. An order with no usable catalogue evidence at all
 * is disclosed as unresolved and blocks nothing; it is never claimed for this
 * game. An order clearly on another game is simply out of scope.
 *
 * Reads run one at a time on one market client (only its latest read wins),
 * under each account's own selection, and every lookup is bounded; reaching a
 * bound is disclosed, never silent.
 */
import type { UsDiscoveryMarket, UsMarketClient } from './marketClient';
import type { UsOrderRow, UsReadiness, UsScope } from './types';

/** What one read established about one saved market slug. */
export type UsSlugEvidence =
  | { kind: 'listed'; market: UsDiscoveryMarket }
  /** The same slug came back with different identities in one read. */
  | { kind: 'conflict'; gameKeys: readonly string[] }
  /** The exact lookup finished and the catalogue does not hold the slug. */
  | { kind: 'not-listed' }
  | { kind: 'unread'; code: string };

export const US_GAME_MAX_CATALOGUE_PAGES = 5;
export const US_GAME_MAX_SLUG_PAGES = 2;
/** Slug lookups per read, across every account. */
export const US_GAME_MAX_SLUG_LOOKUPS = 40;
/** The catalogue's search text limit. */
const SEARCH_MAX = 120;

type EvidenceClient = Pick<UsMarketClient, 'setSelection' | 'listMarkets'>;

const codeOf = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_MARKET_UNAVAILABLE';
const changed = () => Object.assign(new Error('US_CLIENT_CONTEXT_CHANGED'), { code: 'US_CLIENT_CONTEXT_CHANGED' });

/**
 * Catalogue evidence for one account's saved slugs: the game's own markets
 * first (one search by game key), then an exact lookup for each slug that
 * search did not return. `budget.lookups` is shared by every account of one
 * read. A login change mid-read throws; any other failure is evidence.
 */
export async function readUsGameEvidence(
  client: EvidenceClient,
  account: { accountId: string; scope: UsScope },
  usGameKey: string,
  slugs: readonly string[],
  budget: { lookups: number },
  stillCurrent: () => boolean,
): Promise<Map<string, UsSlugEvidence>> {
  const evidence = new Map<string, UsSlugEvidence>();
  if (slugs.length === 0) return evidence;
  const wanted = new Set(slugs);
  const sport = usGameSportForKey(usGameKey);
  if (!sport) {
    if (!stillCurrent()) throw changed();
    return new Map(slugs.map(slug => [slug, { kind: 'unread', code: 'US_CATALOGUE_INVALID_REQUEST' }]));
  }
  const settle = (error: unknown): string => {
    if (!stillCurrent()) throw changed();
    return codeOf(error);
  };
  const record = (market: UsDiscoveryMarket) => {
    if (!wanted.has(market.marketSlug)) return;
    const prior = evidence.get(market.marketSlug);
    // Never first-wins: two identities for one slug is a conflict.
    if (!prior || prior.kind === 'not-listed' || prior.kind === 'unread') {
      evidence.set(market.marketSlug, { kind: 'listed', market });
    } else if (prior.kind === 'listed' && (prior.market.gameKey !== market.gameKey || prior.market.marketId !== market.marketId)) {
      evidence.set(market.marketSlug, { kind: 'conflict', gameKeys: [prior.market.gameKey, market.gameKey] });
    } else if (prior.kind === 'conflict' && !prior.gameKeys.includes(market.gameKey)) {
      evidence.set(market.marketSlug, { kind: 'conflict', gameKeys: [...prior.gameKeys, market.gameKey] });
    }
  };

  client.setSelection({ accountId: account.accountId, scope: account.scope });
  try {
    let after: string | undefined;
    for (let page = 0; page < US_GAME_MAX_CATALOGUE_PAGES; page++) {
      const result = await client.listMarkets({ q: usGameKey, sport, ...(after ? { after } : {}) });
      if (!stillCurrent()) throw changed();
      for (const market of result.markets) record(market);
      if (!result.nextCursor) break;
      after = result.nextCursor;
    }
  } catch (error) {
    const code = settle(error);
    for (const slug of slugs) evidence.set(slug, { kind: 'unread', code });
    return evidence;
  }

  for (const slug of slugs) {
    if (evidence.has(slug)) continue;
    if (slug.length > SEARCH_MAX) { evidence.set(slug, { kind: 'unread', code: 'US_SLUG_NOT_SEARCHABLE' }); continue; }
    if (budget.lookups <= 0) { evidence.set(slug, { kind: 'unread', code: 'US_LOOKUP_LIMIT' }); continue; }
    budget.lookups--;
    try {
      let after: string | undefined;
      let finished = false;
      for (let page = 0; page < US_GAME_MAX_SLUG_PAGES; page++) {
        const result = await client.listMarkets({ q: slug, ...(after ? { after } : {}) });
        if (!stillCurrent()) throw changed();
        for (const market of result.markets) if (market.marketSlug === slug) record(market);
        if (!result.nextCursor) { finished = true; break; }
        after = result.nextCursor;
      }
      if (!evidence.has(slug)) evidence.set(slug, finished ? { kind: 'not-listed' } : { kind: 'unread', code: 'US_LOOKUP_LIMIT' });
    } catch (error) {
      evidence.set(slug, { kind: 'unread', code: settle(error) });
    }
  }
  return evidence;
}

/** One visible account's own open orders, as `readUsOwnOpenOrders` returns them. */
export type UsGameAccountRead =
  | { accountId: string; ok: true; scope: UsScope; readiness: UsReadiness; orders: readonly UsOrderRow[]; truncated: boolean }
  | { accountId: string; ok: false; code: string };

/** An exact-game order to show. `expired`: the listing that names this game
 * has lapsed, so the row is shown but carries no cancel authority. */
export type UsGameOrderRow = { accountId: string; scope: UsScope; readiness: UsReadiness; row: UsOrderRow;
  market: UsDiscoveryMarket; expired: boolean };

/** Orders a line of the dialog accounts for; null count = not known. */
export type UsGameNotice = { accountId: string; orders: number | null; reason: string };

export type UsGameBlocker = { accountId: string; intentId: string; orderId: string | null; label: string; message: string };

export interface UsGameOrderSelection {
  /** Every exact-game order, per account in read order. */
  rows: readonly UsGameOrderRow[];
  /** Per account: the exact-game orders with a current listing, which alone
   * may become cancel targets (their own permission/readiness still decides). */
  sweep: readonly { accountId: string; scope: UsScope; readiness: UsReadiness; orders: readonly UsOrderRow[] }[];
  /** Exact-game orders never sent because their listing lapsed. */
  expired: readonly UsGameNotice[];
  /** Orders tied to this matchup but not to exactly this game. */
  blockers: readonly UsGameBlocker[];
  /** Orders whose game could not be established, and accounts or pages that
   * could not be read. None of these is claimed for this game. */
  unresolved: readonly UsGameNotice[];
}

type KeyParts = { sport: string; date: string; gameNo: string; lo: string; hi: string };
function parts(key: string): KeyParts | null {
  const p = key.split('|');
  return p.length === 5 && /^\d+$/.test(p[2]) ? { sport: p[0], date: p[1], gameNo: p[2], lo: p[3], hi: p[4] } : null;
}

/** A catalogue key that names this matchup's sport, date and both teams but is
 * not an exact game key: which game it means cannot be known. */
function ambiguousForMatchup(key: string, target: KeyParts): boolean {
  if (parts(key)) return false;
  const p = key.split('|');
  return p[0] === target.sport && p.includes(target.date) && p.includes(target.lo) && p.includes(target.hi);
}

const plural = (n: number) => `${n} order${n === 1 ? '' : 's'}`;

/** Pure: split each account's saved orders by this game's catalogue evidence. */
export function selectUsGameOrders(input: {
  usGameKey: string;
  reads: readonly UsGameAccountRead[];
  evidence: ReadonlyMap<string, ReadonlyMap<string, UsSlugEvidence>>;
  nowMs: number;
}): UsGameOrderSelection {
  const target = parts(input.usGameKey);
  const rows: UsGameOrderRow[] = [];
  const sweep: UsGameOrderSelection['sweep'][number][] = [];
  const expired: UsGameNotice[] = [];
  const blockers: UsGameBlocker[] = [];
  const unresolved: UsGameNotice[] = [];

  for (const read of input.reads) {
    if (!read.ok) {
      unresolved.push({ accountId: read.accountId, orders: null, reason: `orders could not be read (${read.code})` });
      continue;
    }
    const evidence = input.evidence.get(read.accountId) ?? new Map<string, UsSlugEvidence>();
    const current: UsOrderRow[] = [];
    let lapsed = 0;
    const unknown = new Map<string, number>();
    const unknownReason = (reason: string) => unknown.set(reason, (unknown.get(reason) ?? 0) + 1);
    for (const row of read.orders) {
      const found = evidence.get(row.target.marketSlug);
      const block = (message: string) => blockers.push({ accountId: read.accountId, intentId: row.intentId,
        orderId: row.orderId, label: row.target.label, message });
      if (!found || found.kind === 'unread') {
        unknownReason(found?.code === 'US_LOOKUP_LIMIT'
          ? 'more markets than one read looks up; not checked'
          : `market could not be looked up (${found?.code ?? 'US_MARKET_UNAVAILABLE'})`);
      } else if (found.kind === 'not-listed') {
        unknownReason('market not in the Polymarket US listing now');
      } else if (found.kind === 'conflict') {
        if (target && found.gameKeys.some((k) => k === input.usGameKey || ambiguousForMatchup(k, target) || sameMatchup(k, target))) {
          block(`Polymarket US order ${row.orderId ?? row.intentId} (${row.target.label}): the listing names more than one game for its market`);
        }
      } else if (target && found.market.gameKey === input.usGameKey) {
        const lapsedNow = found.market.classification.expiresAtMs <= usEvidenceNow(found.market.timeEvidence, input.nowMs);
        rows.push({ accountId: read.accountId, scope: read.scope, readiness: read.readiness, row, market: found.market, expired: lapsedNow });
        if (lapsedNow) lapsed++;
        else current.push(row);
      } else if (target && ambiguousForMatchup(found.market.gameKey, target)) {
        block(`Polymarket US order ${row.orderId ?? row.intentId} (${row.target.label}): its market is listed for this matchup without a game number`);
      }
      // Anything else is listed for another game: out of scope.
    }
    if (current.length > 0) {
      sweep.push({ accountId: read.accountId, scope: read.scope, readiness: read.readiness, orders: current });
    }
    if (lapsed) expired.push({ accountId: read.accountId, orders: lapsed, reason: 'market listing lapsed, so its game can’t be confirmed' });
    for (const [reason, orders] of unknown) unresolved.push({ accountId: read.accountId, orders, reason });
    if (read.truncated) unresolved.push({ accountId: read.accountId, orders: null, reason: 'more saved orders than one read covers; the rest were not read' });
  }
  return { rows, sweep, expired, blockers, unresolved };
}

/** Same sport, date and teams, exact key form: another game of this matchup
 * (a doubleheader's other game) is still "this matchup" for a conflict. */
function sameMatchup(key: string, target: KeyParts): boolean {
  const p = parts(key);
  return !!p && p.sport === target.sport && p.date === target.date && p.lo === target.lo && p.hi === target.hi;
}

/** "Polymarket US: 2 orders not checked, market not in the listing now (account …1234)". */
export function describeUsGameNotices(notices: readonly UsGameNotice[], verb: string): string[] {
  return notices.map(({ accountId, orders, reason }) =>
    `Polymarket US: ${orders === null ? 'orders' : plural(orders)} ${verb}, ${reason} (account …${accountId.slice(-4)})`);
}
